"use client";

import React from "react";
import { motion, type Variants } from "motion/react";
import {
  MapPin,
  Mail,
  Clock,
  Send,
  Loader2,
} from "lucide-react";
import { Toaster } from "sonner";
import { Controller } from "react-hook-form";

import { FormField } from "./FormField";
import { FileUpload } from "./FileUpload";
import { useContactForm } from "@/hooks/useContactFormhome";
import {
  SERVICE_OPTIONS,
  PRODUCT_CATEGORY_OPTIONS,
  COUNTRY_OPTIONS,
} from "@/types/contact";

// ─── Animation Variants ────────────────────────────────────────────────────────

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: EASE,
    },
  },
};

const slideInLeft: Variants = {
  hidden: { opacity: 0, x: -40 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.7,
      ease: EASE,
    },
  },
};

const slideInRight: Variants = {
  hidden: { opacity: 0, x: 40 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.7,
      ease: EASE,
    },
  },
};

// ─── Shared Input Styles ───────────────────────────────────────────────────────
// Fix: Never use `border` shorthand alongside `borderColor`.
// Always use borderWidth + borderStyle + borderColor as separate properties.

const inputBaseStyle: React.CSSProperties = {
  width: "100%",
  background: "rgba(255,255,255,0.04)",
  // Replaced `border: "1px solid rgba(197,168,128,0.2)"` with three separate props
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: "rgba(197,168,128,0.2)",
  borderRadius: "12px",
  padding: "13px 16px",
  fontSize: "14px",
  color: "#e5e7eb",
  outline: "none",
  transition: "border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease",
  fontFamily: "inherit",
  boxSizing: "border-box",
};

const selectAdditionalStyle: React.CSSProperties = {
  appearance: "none",
  backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23C5A880' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 14px center",
  backgroundSize: "16px",
  paddingRight: "42px",
  cursor: "pointer",
};

// ─── Merge Handlers Utility ────────────────────────────────────────────────────

type ReactFocusHandler = React.FocusEventHandler<
  HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
>;

function mergeHandlers(
  handlerA: ReactFocusHandler | undefined,
  handlerB: ReactFocusHandler
): ReactFocusHandler {
  return (e) => {
    handlerA?.(e);
    handlerB(e);
  };
}

// ─── getInputStyle ─────────────────────────────────────────────────────────────
// Fix: Only override `borderColor` — never mix with `border` shorthand.
// All states use the same borderWidth/borderStyle from base, only borderColor changes.

function buildInputStyle(
  fieldName: string,
  hasError: boolean,
  focusedField: string | null,
  isSelect = false
): React.CSSProperties {
  const isFocused = focusedField === fieldName;

  // Resolve borderColor based on state priority: error > focused > default
  let borderColor: string;
  if (hasError && isFocused) {
    borderColor = "rgba(239,68,68,0.8)";
  } else if (hasError) {
    borderColor = "rgba(239,68,68,0.5)";
  } else if (isFocused) {
    borderColor = "rgba(197,168,128,0.6)";
  } else {
    borderColor = "rgba(197,168,128,0.2)";
  }

  // Resolve background based on focus
  const background = isFocused
    ? "rgba(255,255,255,0.06)"
    : "rgba(255,255,255,0.04)";

  // Resolve box-shadow based on focus
  let boxShadow: string;
  if (isFocused && hasError) {
    boxShadow = "0 0 0 3px rgba(239,68,68,0.08)";
  } else if (isFocused) {
    boxShadow = "0 0 0 3px rgba(197,168,128,0.08)";
  } else {
    boxShadow = "none";
  }

  return {
    ...inputBaseStyle,
    ...(isSelect ? selectAdditionalStyle : {}),
    // Only borderColor changes — borderWidth and borderStyle stay from base
    borderColor,
    background,
    boxShadow,
  };
}

// ─── Sub-Components ────────────────────────────────────────────────────────────

function ContactInfoItem({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <motion.div variants={itemVariants} className="flex items-start gap-4">
      <div
        className="flex items-center justify-center w-11 h-11 rounded-full shrink-0 mt-0.5"
        style={{
          background: "rgba(197,168,128,0.12)",
          // Fix: separate border props here too
          borderWidth: "1px",
          borderStyle: "solid",
          borderColor: "rgba(197,168,128,0.25)",
        }}
      >
        {icon}
      </div>
      <div className="flex-1 pt-1">{children}</div>
    </motion.div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export function ContactSection() {
  const {
    register,
    handleSubmit,
    control,
    errors,
    isSubmitting,
    selectedFile,
    handleFileSelect,
    onSubmit,
    onError,
  } = useContactForm();

  const [focusedField, setFocusedField] = React.useState<string | null>(null);

  // ─── Register all fields outside JSX ────────────────────────────────────────

  const fullNameReg = register("fullName", {
    required: "This field is required.",
    minLength: {
      value: 3,
      message: "Name must be at least 3 characters.",
    },
    maxLength: {
      value: 50,
      message: "Name cannot exceed 50 characters.",
    },
  });

  const companyNameReg = register("companyName", {
    required: "This field is required.",
  });

  const businessEmailReg = register("businessEmail", {
    required: "This field is required.",
    pattern: {
      value: /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/,
      message: "Enter a valid email address.",
    },
  });

  const phoneNumberReg = register("phoneNumber", {
    required: "This field is required.",
    pattern: {
      value: /^[\+\d\s]{8,15}$/,
      message: "Phone number is invalid.",
    },
    validate: {
      digitsOnly: (value: string) => {
        const digits = value.replace(/[\+\s]/g, "");
        if (digits.length < 8) return "Minimum 8 digits required.";
        if (digits.length > 15) return "Maximum 15 digits allowed.";
        return true;
      },
    },
  });

  const countryReg = register("country", {
    required: "This field is required.",
  });

  const serviceRequiredReg = register("serviceRequired", {
    required: "This field is required.",
  });

  const productCategoryReg = register("productCategory", {
    required: "This field is required.",
  });

  const estimatedQuantityReg = register("estimatedQuantity");

  const messageReg = register("message", {
    required: "This field is required.",
    minLength: {
      value: 30,
      message: "Message must be at least 30 characters.",
    },
    maxLength: {
      value: 1000,
      message: "Message cannot exceed 1000 characters.",
    },
  });

  const privacyPolicyReg = register("privacyPolicy", {
    required: "You must agree to the privacy policy.",
  });

  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: "#0f0f0f",
            // Fix: separate border props on Toaster style too
            borderWidth: "1px",
            borderStyle: "solid",
            borderColor: "rgba(197,168,128,0.3)",
            color: "#e5e7eb",
            borderRadius: "12px",
          },
        }}
      />

      <section
        className="w-full min-h-screen flex items-center justify-center px-4 py-20 relative overflow-hidden"
        style={{ background: "#080808" }}
        aria-labelledby="contact-heading"
      >
        {/* Background Decorations */}
        <div
          className="absolute inset-0 pointer-events-none"
          aria-hidden="true"
        >
          <div
            className="absolute top-0 left-1/4 w-96 h-96 rounded-full opacity-10 blur-3xl"
            style={{
              background: "radial-gradient(circle, #C5A880, transparent)",
            }}
          />
          <div
            className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full opacity-10 blur-3xl"
            style={{
              background: "radial-gradient(circle, #C5A880, transparent)",
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "radial-gradient(rgba(197,168,128,0.04) 1px, transparent 1px)",
              backgroundSize: "48px 48px",
            }}
          />
        </div>

        <div className="relative w-full max-w-7xl mx-auto">
          <div className="grid gap-8 items-start lg:grid-cols-2">

            {/* ── LEFT COLUMN ── */}
            <motion.div
              variants={slideInLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              className="flex flex-col py-8 lg:py-12"
            >
              {/* Badge */}
              <motion.div variants={itemVariants} className="mb-8">
                <div
                  className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full text-xs font-bold tracking-widest uppercase"
                  style={{
                    background: "rgba(197,168,128,0.1)",
                    borderWidth: "1px",
                    borderStyle: "solid",
                    borderColor: "rgba(197,168,128,0.25)",
                    color: "#C5A880",
                  }}
                >
                  <span
                    className="flex items-center justify-center w-5 h-5 rounded-full text-[9px] font-black"
                    style={{ background: "#C5A880", color: "#080808" }}
                  >
                    TQC
                  </span>
                  Get In Touch
                </div>
              </motion.div>

              <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                {/* Heading */}
                <motion.h2
                  variants={itemVariants}
                  id="contact-heading"
                  className="font-black tracking-tight mb-6"
                  style={{
                    fontSize: "clamp(2.4rem, 4vw, 3.6rem)",
                    color: "#f5f5f5",
                    lineHeight: 1.08,
                  }}
                >
                  Let&apos;s Build Your
                  <br />
                  <span style={{ color: "#C5A880" }}>Next Collection</span>
                </motion.h2>

                {/* Description */}
                <motion.p
                  variants={itemVariants}
                  className="text-sm mb-12 max-w-sm"
                  style={{
                    color: "rgba(229,231,235,0.5)",
                    lineHeight: "1.75",
                  }}
                >
                  Whether you&apos;re looking for custom manufacturing, OEM,
                  Private Label, Product Development, or Export Solutions —
                  our team is ready to help you create exceptional products.
                </motion.p>

                {/* Contact Info */}
                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  className="flex flex-col gap-6"
                >
                  <ContactInfoItem
                    icon={<MapPin size={18} style={{ color: "#C5A880" }} />}
                  >
                    <p
                      className="text-xs font-bold uppercase tracking-widest mb-2"
                      style={{ color: "#C5A880" }}
                    >
                      Address
                    </p>
                    <address
                      className="not-italic text-sm leading-relaxed"
                      style={{ color: "rgba(229,231,235,0.65)" }}
                    >
                      THE QUALITY COLLECTION
                      <br />
                      Near Behind Petrol Pump
                      <br />
                      Tajpur Mafi, Dalpatpur Road
                      <br />
                      Moradabad, Uttar Pradesh 244001
                      <br />
                      India
                    </address>
                  </ContactInfoItem>

                  <ContactInfoItem
                    icon={<Mail size={18} style={{ color: "#C5A880" }} />}
                  >
                    <p
                      className="text-xs font-bold uppercase tracking-widest mb-2"
                      style={{ color: "#C5A880" }}
                    >
                      Email Us
                    </p>
                    <div className="flex flex-col gap-1">
                      {[
                        "priority@thequalitycollection.com",
                        "sales@thequalitycollection.com",
                        "care@thequalitycollection.com",
                      ].map((email) => (
                        <a
                          key={email}
                          href={`mailto:${email}`}
                          className="text-sm transition-colors duration-200"
                          style={{ color: "rgba(229,231,235,0.65)" }}
                          onMouseEnter={(e) => {
                            (
                              e.currentTarget as HTMLAnchorElement
                            ).style.color = "#C5A880";
                          }}
                          onMouseLeave={(e) => {
                            (
                              e.currentTarget as HTMLAnchorElement
                            ).style.color = "rgba(229,231,235,0.65)";
                          }}
                        >
                          {email}
                        </a>
                      ))}
                    </div>
                  </ContactInfoItem>

                  <ContactInfoItem
                    icon={<Clock size={18} style={{ color: "#C5A880" }} />}
                  >
                    <p
                      className="text-xs font-bold uppercase tracking-widest mb-2"
                      style={{ color: "#C5A880" }}
                    >
                      Business Hours
                    </p>
                    <p
                      className="text-sm"
                      style={{ color: "rgba(229,231,235,0.65)" }}
                    >
                      Monday – Saturday
                      <br />
                      9:00 AM – 6:30 PM IST
                    </p>
                  </ContactInfoItem>
                </motion.div>
              </motion.div>
            </motion.div>

            {/* ── RIGHT COLUMN — Glass Card ── */}
            <motion.div
              variants={slideInRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              className="relative rounded-3xl p-8 lg:p-10"
              style={{
                background:
                  "linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)",
                // Fix: separate border props on glass card
                borderWidth: "1px",
                borderStyle: "solid",
                borderColor: "rgba(197,168,128,0.18)",
                backdropFilter: "blur(24px)",
                WebkitBackdropFilter: "blur(24px)",
                boxShadow:
                  "0 24px 80px rgba(0,0,0,0.5), inset 0 1px 0 rgba(197,168,128,0.12)",
              }}
            >
              {/* Gold top border accent */}
              <div
                className="absolute top-0 left-8 right-8 h-px rounded-full"
                style={{
                  background:
                    "linear-gradient(90deg, transparent, rgba(197,168,128,0.6), transparent)",
                }}
                aria-hidden="true"
              />

              <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <motion.p
                  variants={itemVariants}
                  className="text-xs font-bold uppercase tracking-widest mb-8"
                  style={{ color: "#C5A880" }}
                >
                  Request a Quotation
                </motion.p>

                <form
                  onSubmit={handleSubmit(onSubmit, onError)}
                  noValidate
                  aria-label="Contact form"
                >
                  <motion.div
                    variants={containerVariants}
                    className="flex flex-col gap-5"
                  >

                    {/* ── Row 1: Full Name + Company ── */}
                    <motion.div
                      variants={itemVariants}
                      className="grid grid-cols-1 gap-5 sm:grid-cols-2"
                    >
                      <FormField
                        id="fullName"
                        label="Full Name"
                        required
                        error={errors.fullName?.message}
                      >
                        <input
                          id="fullName"
                          type="text"
                          placeholder="John Doe"
                          autoComplete="name"
                          aria-required="true"
                          aria-describedby={
                            errors.fullName ? "fullName-error" : undefined
                          }
                          style={buildInputStyle(
                            "fullName",
                            !!errors.fullName,
                            focusedField
                          )}
                          onFocus={() => setFocusedField("fullName")}
                          {...fullNameReg}
                          onBlur={mergeHandlers(fullNameReg.onBlur, () =>
                            setFocusedField(null)
                          )}
                        />
                      </FormField>

                      <FormField
                        id="companyName"
                        label="Company Name"
                        required
                        error={errors.companyName?.message}
                      >
                        <input
                          id="companyName"
                          type="text"
                          placeholder="Your Company Ltd."
                          autoComplete="organization"
                          aria-required="true"
                          aria-describedby={
                            errors.companyName
                              ? "companyName-error"
                              : undefined
                          }
                          style={buildInputStyle(
                            "companyName",
                            !!errors.companyName,
                            focusedField
                          )}
                          onFocus={() => setFocusedField("companyName")}
                          {...companyNameReg}
                          onBlur={mergeHandlers(companyNameReg.onBlur, () =>
                            setFocusedField(null)
                          )}
                        />
                      </FormField>
                    </motion.div>

                    {/* ── Row 2: Email + Phone ── */}
                    <motion.div
                      variants={itemVariants}
                      className="grid grid-cols-1 gap-5 sm:grid-cols-2"
                    >
                      <FormField
                        id="businessEmail"
                        label="Business Email"
                        required
                        error={errors.businessEmail?.message}
                      >
                        <input
                          id="businessEmail"
                          type="email"
                          placeholder="you@company.com"
                          autoComplete="email"
                          aria-required="true"
                          aria-describedby={
                            errors.businessEmail
                              ? "businessEmail-error"
                              : undefined
                          }
                          style={buildInputStyle(
                            "businessEmail",
                            !!errors.businessEmail,
                            focusedField
                          )}
                          onFocus={() => setFocusedField("businessEmail")}
                          {...businessEmailReg}
                          onBlur={mergeHandlers(
                            businessEmailReg.onBlur,
                            () => setFocusedField(null)
                          )}
                        />
                      </FormField>

                      <FormField
                        id="phoneNumber"
                        label="Phone Number"
                        required
                        error={errors.phoneNumber?.message}
                      >
                        <input
                          id="phoneNumber"
                          type="tel"
                          placeholder="+1 800 000 0000"
                          autoComplete="tel"
                          aria-required="true"
                          aria-describedby={
                            errors.phoneNumber
                              ? "phoneNumber-error"
                              : undefined
                          }
                          style={buildInputStyle(
                            "phoneNumber",
                            !!errors.phoneNumber,
                            focusedField
                          )}
                          onFocus={() => setFocusedField("phoneNumber")}
                          {...phoneNumberReg}
                          onBlur={mergeHandlers(phoneNumberReg.onBlur, () =>
                            setFocusedField(null)
                          )}
                        />
                      </FormField>
                    </motion.div>

                    {/* ── Row 3: Country + Service ── */}
                    <motion.div
                      variants={itemVariants}
                      className="grid grid-cols-1 gap-5 sm:grid-cols-2"
                    >
                      <FormField
                        id="country"
                        label="Country"
                        required
                        error={errors.country?.message}
                      >
                        <select
                          id="country"
                          aria-required="true"
                          aria-describedby={
                            errors.country ? "country-error" : undefined
                          }
                          style={buildInputStyle(
                            "country",
                            !!errors.country,
                            focusedField,
                            true
                          )}
                          onFocus={() => setFocusedField("country")}
                          {...countryReg}
                          onBlur={mergeHandlers(countryReg.onBlur, () =>
                            setFocusedField(null)
                          )}
                        >
                          {COUNTRY_OPTIONS.map((option) => (
                            <option
                              key={option.value}
                              value={option.value}
                              disabled={option.value === ""}
                              style={{
                                background: "#141414",
                                color:
                                  option.value === ""
                                    ? "rgba(229,231,235,0.3)"
                                    : "#e5e7eb",
                              }}
                            >
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </FormField>

                      <FormField
                        id="serviceRequired"
                        label="Service Required"
                        required
                        error={errors.serviceRequired?.message}
                      >
                        <select
                          id="serviceRequired"
                          aria-required="true"
                          aria-describedby={
                            errors.serviceRequired
                              ? "serviceRequired-error"
                              : undefined
                          }
                          style={buildInputStyle(
                            "serviceRequired",
                            !!errors.serviceRequired,
                            focusedField,
                            true
                          )}
                          onFocus={() => setFocusedField("serviceRequired")}
                          {...serviceRequiredReg}
                          onBlur={mergeHandlers(
                            serviceRequiredReg.onBlur,
                            () => setFocusedField(null)
                          )}
                        >
                          {SERVICE_OPTIONS.map((option) => (
                            <option
                              key={option.value}
                              value={option.value}
                              disabled={option.value === ""}
                              style={{
                                background: "#141414",
                                color:
                                  option.value === ""
                                    ? "rgba(229,231,235,0.3)"
                                    : "#e5e7eb",
                              }}
                            >
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </FormField>
                    </motion.div>

                    {/* ── Row 4: Category + Quantity ── */}
                    <motion.div
                      variants={itemVariants}
                      className="grid grid-cols-1 gap-5 sm:grid-cols-2"
                    >
                      <FormField
                        id="productCategory"
                        label="Product Category"
                        required
                        error={errors.productCategory?.message}
                      >
                        <select
                          id="productCategory"
                          aria-required="true"
                          aria-describedby={
                            errors.productCategory
                              ? "productCategory-error"
                              : undefined
                          }
                          style={buildInputStyle(
                            "productCategory",
                            !!errors.productCategory,
                            focusedField,
                            true
                          )}
                          onFocus={() => setFocusedField("productCategory")}
                          {...productCategoryReg}
                          onBlur={mergeHandlers(
                            productCategoryReg.onBlur,
                            () => setFocusedField(null)
                          )}
                        >
                          {PRODUCT_CATEGORY_OPTIONS.map((option) => (
                            <option
                              key={option.value}
                              value={option.value}
                              disabled={option.value === ""}
                              style={{
                                background: "#141414",
                                color:
                                  option.value === ""
                                    ? "rgba(229,231,235,0.3)"
                                    : "#e5e7eb",
                              }}
                            >
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </FormField>

                      <FormField
                        id="estimatedQuantity"
                        label="Estimated Quantity"
                        error={errors.estimatedQuantity?.message}
                      >
                        <input
                          id="estimatedQuantity"
                          type="text"
                          placeholder="e.g. 500 units"
                          style={buildInputStyle(
                            "estimatedQuantity",
                            !!errors.estimatedQuantity,
                            focusedField
                          )}
                          onFocus={() => setFocusedField("estimatedQuantity")}
                          {...estimatedQuantityReg}
                          onBlur={mergeHandlers(
                            estimatedQuantityReg.onBlur,
                            () => setFocusedField(null)
                          )}
                        />
                      </FormField>
                    </motion.div>

                    {/* ── Message ── */}
                    <motion.div variants={itemVariants}>
                      <FormField
                        id="message"
                        label="Message"
                        required
                        error={errors.message?.message}
                      >
                        <textarea
                          id="message"
                          rows={4}
                          placeholder="Tell us about your project, requirements, and any specific details..."
                          aria-required="true"
                          aria-describedby={
                            errors.message ? "message-error" : undefined
                          }
                          style={{
                            ...buildInputStyle(
                              "message",
                              !!errors.message,
                              focusedField
                            ),
                            resize: "vertical",
                            minHeight: "110px",
                          }}
                          onFocus={() => setFocusedField("message")}
                          {...messageReg}
                          onBlur={mergeHandlers(messageReg.onBlur, () =>
                            setFocusedField(null)
                          )}
                        />
                      </FormField>
                    </motion.div>

                    {/* ── File Upload ── */}
                    <motion.div variants={itemVariants}>
                      <Controller
                        name="referenceFile"
                        control={control}
                        render={() => (
                          <FileUpload
                            onFileSelect={handleFileSelect}
                            selectedFile={selectedFile}
                          />
                        )}
                      />
                    </motion.div>

                    {/* ── Privacy Policy ── */}
                    <motion.div variants={itemVariants}>
                      <div className="flex items-start gap-3">
                        <div className="relative mt-0.5 shrink-0">
                          <input
                            id="privacyPolicy"
                            type="checkbox"
                            className="sr-only"
                            aria-required="true"
                            aria-describedby={
                              errors.privacyPolicy
                                ? "privacyPolicy-error"
                                : undefined
                            }
                            {...privacyPolicyReg}
                          />
                          <label
                            htmlFor="privacyPolicy"
                            className="flex items-center justify-center w-5 h-5 rounded cursor-pointer transition-all duration-200"
                            style={{
                              // Fix: separate border props on checkbox label
                              borderWidth: "1.5px",
                              borderStyle: "solid",
                              borderColor: errors.privacyPolicy
                                ? "rgba(239,68,68,0.6)"
                                : "rgba(197,168,128,0.35)",
                              background: "rgba(197,168,128,0.05)",
                            }}
                            aria-hidden="true"
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="privacyPolicy"
                            className="text-xs leading-relaxed cursor-pointer"
                            style={{ color: "rgba(229,231,235,0.55)" }}
                          >
                            I agree to the{" "}
                            <span
                              className="underline"
                              style={{ color: "#C5A880" }}
                            >
                              Privacy Policy
                            </span>{" "}
                            and consent to The Quality Collection processing
                            my inquiry.
                          </label>
                          {errors.privacyPolicy && (
                            <motion.p
                              initial={{ opacity: 0, y: -4 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="text-xs font-medium mt-1"
                              style={{ color: "#ef4444" }}
                              role="alert"
                              aria-live="polite"
                              id="privacyPolicy-error"
                            >
                              {errors.privacyPolicy.message}
                            </motion.p>
                          )}
                        </div>
                      </div>
                    </motion.div>

                    {/* ── Submit Button ── */}
                    <motion.div variants={itemVariants} className="pt-2">
                      <motion.button
                        type="submit"
                        disabled={isSubmitting}
                        whileHover={
                          isSubmitting ? {} : { scale: 1.02, y: -1 }
                        }
                        whileTap={isSubmitting ? {} : { scale: 0.98 }}
                        transition={{ duration: 0.2 }}
                        className="relative w-full flex items-center justify-center gap-3 py-4 px-8 rounded-2xl text-sm font-bold uppercase overflow-hidden"
                        style={{
                          background: isSubmitting
                            ? "rgba(197,168,128,0.4)"
                            : "linear-gradient(135deg, #C5A880 0%, #a8895e 100%)",
                          color: isSubmitting
                            ? "rgba(8,8,8,0.5)"
                            : "#080808",
                          cursor: isSubmitting ? "not-allowed" : "pointer",
                          borderWidth: "0px",
                          borderStyle: "solid",
                          borderColor: "transparent",
                          boxShadow: isSubmitting
                            ? "none"
                            : "0 8px 32px rgba(197,168,128,0.25)",
                          letterSpacing: "0.08em",
                        }}
                        aria-disabled={isSubmitting}
                        aria-label={
                          isSubmitting
                            ? "Submitting your request…"
                            : "Request quotation"
                        }
                      >
                        {/* Shimmer */}
                        {!isSubmitting && (
                          <motion.div
                            className="absolute inset-0 opacity-0"
                            whileHover={{ opacity: 1 }}
                            style={{
                              background:
                                "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.18) 50%, transparent 60%)",
                            }}
                          />
                        )}

                        {isSubmitting ? (
                          <>
                            <Loader2
                              size={18}
                              className="animate-spin shrink-0"
                            />
                            <span>Submitting…</span>
                          </>
                        ) : (
                          <>
                            <Send
                              size={16}
                              className="shrink-0"
                              aria-hidden="true"
                            />
                            <span>Request Quotation</span>
                          </>
                        )}
                      </motion.button>
                    </motion.div>

                  </motion.div>
                </form>
              </motion.div>
            </motion.div>

          </div>
        </div>
      </section>

      <style>{`
        input::placeholder,
        textarea::placeholder {
          color: rgba(229, 231, 235, 0.28);
        }
        input[type="checkbox"]:checked + label {
          background: #C5A880 !important;
          border-color: #C5A880 !important;
        }
        input[type="checkbox"]:focus-visible + label {
          outline: 2px solid #C5A880;
          outline-offset: 2px;
        }
        select option {
          background: #141414;
          color: #e5e7eb;
        }
        textarea {
          scrollbar-width: thin;
          scrollbar-color: rgba(197,168,128,0.3) transparent;
        }
      `}</style>
    </>
  );
}